

import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { login as reduxLogin, getMyPermissions } from "../redux/actions/authActions";
import { useAuth } from "../context/AuthContext";
import LogoImg from "../images/ebs.png";
import { 
  FiUser, 
  FiLock, 
  FiEye, 
  FiEyeOff, 
  FiLogIn, 
  FiShield, 
  FiBarChart2, 
  FiHeadphones,
  FiCheckCircle,
  FiSmartphone,
  FiSun,
  FiMoon
} from "react-icons/fi";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { login } = useAuth();

  // Initialize dark mode from localStorage or system preference
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedDarkMode === 'true' || (!savedDarkMode && systemPrefersDark)) {
      setDarkMode(true);
      document.documentElement.classList.add('dark');
    } else {
      setDarkMode(false);
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    try {
      const response = await dispatch(reduxLogin(username, password));

      if (response?.meta?.status) {
        const {
          access_token,
          id,
          username: name,
          phone,
          role,
          municipality,
          district,
        } = response.data;

        localStorage.setItem("userId", id);
        localStorage.setItem("name", name);
        localStorage.setItem("phone", phone);
        localStorage.setItem("municipality", municipality);
        localStorage.setItem("district", district);

        if (rememberMe) {
          localStorage.setItem("rememberMe", "true");
        }

        login(access_token, role);
        await dispatch(getMyPermissions());

        toast.success("Login successful! Welcome back.");
        navigate("/dashboard");
      } else {
        toast.error(response?.meta?.message || "Invalid credentials. Please try again.");
      }
    } catch (err) {
      toast.error(err.message || "Login error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen font-sans bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900 flex items-center justify-center p-4 transition-colors duration-300">
      {/* Dark Mode Toggle Button */}
      <button
        onClick={toggleDarkMode}
        className="fixed top-6 right-6 z-50 p-3 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700 group"
        title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
      >
        {darkMode ? (
          <FiSun className="w-5 h-5 text-yellow-500 group-hover:scale-110 transition-transform" />
        ) : (
          <FiMoon className="w-5 h-5 text-gray-700 group-hover:scale-110 transition-transform" />
        )}
      </button>

      <div className="w-full max-w-6xl flex flex-col lg:flex-row rounded-3xl overflow-hidden shadow-2xl bg-white dark:bg-gray-800 transition-colors duration-300">
        {/* Left Side - Brand Section */}
        <div className="lg:w-1/2 relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 p-8 lg:p-12 flex flex-col justify-center">
          {/* Animated Background Elements */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
            <div className="absolute -top-24 -left-24 w-48 h-48 bg-white/10 rounded-full blur-xl animate-pulse"></div>
            <div className="absolute -bottom-24 -right-24 w-48 h-48 bg-white/10 rounded-full blur-xl animate-pulse delay-1000"></div>
            <div className="absolute top-1/2 right-1/4 w-32 h-32 bg-white/5 rounded-full blur-lg animate-pulse delay-500"></div>
            <div className="absolute bottom-1/4 left-1/4 w-24 h-24 bg-white/5 rounded-full blur-lg animate-pulse delay-1500"></div>
          </div>
          
          {/* Content */}
          <div className="relative z-10 text-center lg:text-left">
            <div className="flex justify-center lg:justify-start mb-6">
              <div className="relative">
                <img
                  className="w-16 h-16 lg:w-20 lg:h-20 drop-shadow-2xl transform hover:scale-105 transition-transform duration-300"
                  src={LogoImg}
                  alt="Ebluesys Logo"
                />
                <div className="absolute -inset-2 bg-blue-400/20 rounded-full blur-md"></div>
              </div>
            </div>
            
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
              Welcome to{" "}
              <span className="block mt-2 bg-gradient-to-r from-cyan-300 to-blue-200 bg-clip-text text-transparent">
                EBLUESYS
              </span>
            </h1>
            
            <p className="text-blue-100 text-lg lg:text-xl mb-8 leading-relaxed max-w-md mx-auto lg:mx-0">
              Empowering Businesses Digitally with cutting-edge enterprise solutions
            </p>
            
            {/* Features List */}
            <div className="space-y-4">
              {[
                { icon: FiShield, text: "Enterprise-grade Security", color: "text-cyan-300" },
                { icon: FiBarChart2, text: "Real-time Analytics Dashboard", color: "text-blue-300" },
                { icon: FiHeadphones, text: "24/7 Premium Support", color: "text-indigo-300" },
                { icon: FiSmartphone, text: "Mobile Responsive Design", color: "text-purple-300" }
              ].map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-center lg:justify-start space-x-3 text-blue-100 group hover:text-white transition-colors duration-200"
                >
                  <feature.icon className={`w-5 h-5 ${feature.color} group-hover:scale-110 transition-transform duration-200`} />
                  <span className="text-sm lg:text-base">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* Trust Badge */}
            <div className="mt-8 flex items-center justify-center lg:justify-start space-x-2 text-blue-200 text-sm">
              <FiCheckCircle className="w-4 h-4" />
              <span>Trusted by 500+ businesses worldwide</span>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="lg:w-1/2 p-8 lg:p-12 flex items-center justify-center">
          <div className="w-full max-w-md">
            <div className="text-center lg:text-left mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300">
                Welcome Back
              </h2>
              <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">
                Sign in to your account to continue
              </p>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Username Field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors duration-300">
                  Username or Phone
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiUser className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-300" />
                  </div>
                  <input
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 placeholder-gray-400 dark:placeholder-gray-500 dark:text-white"
                    placeholder="Enter your username or phone number"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors duration-300">
                  Password
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiLock className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-300" />
                  </div>
                  <input
                    className="w-full pl-10 pr-12 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 placeholder-gray-400 dark:placeholder-gray-500 dark:text-white"
                    placeholder="Enter your password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-300"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Remember Me */}
              <div className="flex items-center justify-between">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-colors duration-300"
                    disabled={loading}
                  />
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400 transition-colors duration-300">Remember me</span>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !username || !password}
                className="w-full group relative bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3.5 px-4 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:transform-none disabled:hover:shadow-lg disabled:cursor-not-allowed"
              >
                <div className="flex items-center justify-center space-x-2">
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Signing in...</span>
                    </>
                  ) : (
                    <>
                      <FiLogIn className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                      <span>Sign In to Dashboard</span>
                    </>
                  )}
                </div>
                
                {/* Button Shine Effect */}
                <div className="absolute inset-0 rounded-xl overflow-hidden">
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full group-hover:duration-1000 duration-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                </div>
              </button>
            </form>

            {/* Footer */}
          
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 transition-colors duration-300">
  <p className="text-center text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">
    © {new Date().getFullYear()}{" "}
    <a
      href="https://www.ebluesys.com"
      target="_blank"
      rel="noopener noreferrer"
      className="font-semibold text-blue-600 dark:text-blue-400 hover:underline transition-colors duration-300"
    >
      Ebluesys
    </a>.
    <br className="sm:hidden" /> All rights reserved.
  </p>
</div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;